import { ApolloClient, ApolloLink, createHttpLink, fromPromise, InMemoryCache } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { appStore, authStore, notifyStore, selfModeStore } from '~/stores'
import { toJS } from '~/common/mobx.decorator'
import { EVENTS, PATHS } from '../constants'
import { eventBus } from 'mobx-event-bus2'
import ApolloLinkTimeout from 'apollo-link-timeout'
import { captureException, captureFatalException } from '../helpers'
// Shared with the Candidates tab, which hides itself on the same verdict (SPT-2164).
import { isUnsupportedFieldError } from '~/features/recruiter-cadences/candidates.utils'
import { USER_MESSAGES, isForbiddenError, isTransientError, markNotified, wasNotified } from '~/common/helpers/user-message.helper'

const timeout = new ApolloLinkTimeout(60000)

// Login surface the request originates from, for MAU-by-access-point reporting.
// Generic by design: Maestro is embedded inside several ATS (Target Recruit /
// Salesforce Canvas, Bullhorn, ...). Any iframe/embedded context → "embedded";
// the direct portal → "standalone". The backend stores whatever slug we send, so
// this can later return a specific ATS source without a backend change.
const resolveAccessPoint = () => {
	try {
		const inIframe = window.self !== window.top
		const embeddedParam = new URLSearchParams(window.location.search).get('embedded') === 'true'
		return inIframe || embeddedParam ? 'embedded' : 'standalone'
	} catch (e) {
		// Cross-origin access to window.top is blocked → we are almost certainly embedded.
		return 'embedded'
	}
}

const authLink = setContext((_, { headers = {} }) => {
	const token = toJS(authStore.authorization)
	const customHeaders = {}

	if (token) {
		customHeaders['Authorization'] = token
	}

	// Tag the access point (standalone portal vs embedded ATS) for MAU tracking.
	customHeaders['X-Access-Point'] = resolveAccessPoint()

	// Add impersonation header if exists
	const impersonateId = localStorage.getItem('impersonateRecruiterId')
	if (impersonateId) {
		customHeaders['X-Impersonate-Recruiter-Id'] = impersonateId
	}

	// Add Self Mode header if the stored choice belongs to whoever is signed in.
	// Same rule as isSelfModeActive in self-mode.store.js — inlined because the
	// specs that mock '~/stores' hand this link a bare `{ enabled }` object.
	const selfModeEnabled = selfModeStore?.enabled
	const selfModeOwner = selfModeStore?.ownerId
	const profileId = authStore?.profile?.id
	if (selfModeEnabled && (selfModeOwner == null || profileId == null || selfModeOwner === profileId)) {
		customHeaders['X-Self-Mode'] = 'true'
	}

	customHeaders['GraphiQL_Authorization'] = process.env.REACT_APP_PROXY_GRAPHQL_AUTHENTICATION

	return { headers: { ...customHeaders, ...headers } }
})

// AI_ASSIST_FAILED: a refused or failed AI draft (both AI fields), shown in the AI assist panel.
// CADENCE_INVALID_DEFINITION: a workflow the validator refused (Create with AI's draft), listed by the page.
export const INLINE_ERROR_CODES = ['VALIDATION_ERROR', 'REASON_REQUIRED', 'RATE_LIMITED', 'NOT_FOUND', 'AI_ASSIST_FAILED', 'CADENCE_INVALID_DEFINITION']

/*
 * `context: { silentErrors: true }` marks a request whose failure the caller
 * expects and handles itself — a capability probe that asks whether the server
 * knows a new field or enum value, or an optional best-effort feature such as an
 * in-app walkthrough. Its GraphQL errors are the answer, not a
 * fault: no toast for the recruiter, no Sentry report. Auth (401 / Invalid
 * Token) still goes through the normal path.
 */
export const isSilentOperation = (operation) => Boolean(operation?.getContext?.()?.silentErrors)

export const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
	if (graphQLErrors?.[0]?.message === 'Invalid Token') {
		eventBus.post(EVENTS.authStore.logout)
		return
	}

	if (networkError?.statusCode === 401) {
		if (operation.operationName === 'refreshToken') return

		return fromPromise(
			authStore
				.refreshAuthToken()
				.then((authToken) => {
					if (authToken?.accessToken) {
						const oldHeaders = operation.getContext().headers
						operation.setContext({
							headers: {
								...oldHeaders,
								Authorization: `${authToken.tokenType === 'bearer' ? 'Bearer' : ''} ${authToken.accessToken}`,
							},
						})
						return forward(operation)
					}
					authStore.logout()
					return Promise.reject(new Error('Failed to refresh token'))
				})
				.catch((error) => {
					authStore.logout()
					return Promise.reject(error)
				})
		).flatMap((result) => result)
	}

	if (isSilentOperation(operation)) return

	// Deploy skew: the portal can ship ahead of the Maestro gem, and some tenants run a gem branch
	// without a given feature. Asking for a field the connected schema does not have fails GraphQL
	// *validation*, so the whole document is rejected. The feature that selected it handles that
	// itself by hiding its UI; a toast here would tell the admin something is broken when nothing is.
	if (isUnsupportedFieldError(graphQLErrors)) return

	// Business answers the calling screen shows next to the field or in its dialog (a refused save, a
	// High-impact change without a reason, a test-send limit, a notification that no longer exists). Only
	// operations that opt in with `context: { inlineErrors: true }` are spared the toast; everywhere else
	// nothing changes.
	const { inlineErrors } = operation.getContext()
	if (inlineErrors === true && INLINE_ERROR_CODES.includes(graphQLErrors?.[0]?.extensions?.code)) return

	const firstError = graphQLErrors?.[0]
	const serverFailure = firstError?.extensions?.code === 500

	// `inlineErrors: 'all'`: the screen shows every refusal in place, with or without a code (Change with AI), so
	// none is toasted or sent to Sentry. A server failure is still reported; the screen's catch marks it handled.
	if (inlineErrors === 'all' && firstError && !serverFailure) return

	if (serverFailure) {
		captureFatalException('[API Response Error]', { graphQLErrors, networkError, operation })
	} else if (firstError) {
		const handledCodes = ['EMPLOYER_ALREADY_EXISTS']
		if (!handledCodes.includes(firstError?.extensions?.code)) {
			captureException('Apollo Client', { graphQLErrors, networkError, operation })
		}
	}

	if (!firstError && !networkError) return
	reportFailure({ operation, graphQLErrors, networkError, serverFailure })
})

/*
 * Who tells the recruiter that a request failed.
 *
 * The screen that made the request knows what it was for, so it speaks first:
 * `notifyStore.error(error, { fallback: 'Couldn’t save the template.' })` in its
 * catch, or an error state in place of the data. This link waits a beat and
 * speaks only for failures no screen reported, so one failure is one message.
 *
 * - A change the recruiter asked for (a mutation) gets an error toast in plain
 *   words: the server's sentence when it is written for people, ours otherwise.
 * - Data loaded in the background (a query) never says "you do not have
 *   permission" — the screen should not have offered what the person cannot see.
 *   Any other loading failure gets one quiet warning, however many queries failed;
 *   it offers Refresh only when a reload can help (offline, 5xx, timeout), not for
 *   a refusal the server would repeat.
 * - Being offline is the connection banner's to report; a failed save while
 *   offline still says the save did not happen. A server that answered with an
 *   error status is reported like any other failure.
 */
export const LINK_REPORT_DELAY_MS = 400

export const isMutationOperation = (operation) =>
	Boolean(operation?.query?.definitions?.some((definition) => definition?.kind === 'OperationDefinition' && definition?.operation === 'mutation'))

export const reportFailure = ({ operation, graphQLErrors, networkError, serverFailure }) => {
	const failure = { graphQLErrors: graphQLErrors || [], networkError }
	const mutation = isMutationOperation(operation)

	setTimeout(() => {
		if (wasNotified(failure)) return

		if (mutation) {
			if (serverFailure) {
				// `cause`: a screen that reports the same failure later does not add a second message.
				notifyStore.error(USER_MESSAGES.server, { cause: failure })
				return
			}
			notifyStore.error(graphQLErrors?.length ? graphQLErrors[0] : failure)
			return
		}

		// Offline is the connection banner's to say. A server that answered with an error status (502, 500, 403)
		// is a failure like any other: before, any network-level failure of a query was silent.
		if (networkError && !graphQLErrors?.length && typeof navigator !== 'undefined' && navigator.onLine === false) return
		if (isForbiddenError(failure)) return
		markNotified(failure)
		// Refresh is offered only when a reload can help (connection, server down, timeout). A refusal the
		// server will repeat on every reload (SSI-447: the Superset dashboard) must not promise that it will.
		if (serverFailure || isTransientError(failure)) {
			notifyStore.warning(USER_MESSAGES.loadFailed, { action: { label: 'Refresh', onClick: () => window.location.reload() } })
			return
		}
		notifyStore.warning(USER_MESSAGES.loadRefused)
	}, LINK_REPORT_DELAY_MS)
}

const defaultOptions = {
	watchQuery: {
		fetchPolicy: 'no-cache',
		errorPolicy: 'ignore',
	},
	query: {
		fetchPolicy: 'no-cache',
		errorPolicy: 'all',
	},
}

const publicHttpLinkFunc = (url) => {
	return createHttpLink({
		uri: `${url}${PATHS.common.public_graphql}`,
		fetchOptions: {},
	})
}

const httpLinkFunc = (url) => {
	return createHttpLink({
		uri: `${url}${PATHS.common.api}${PATHS.common.graphql}${PATHS.common.recruiter}`,
		fetchOptions: {},
	})
}

// Timecards and pay are served by the main app's candidate schema, not by the
// Maestro engine.
//
// The engine has no timesheet, worked-shift or pay-package types at all — its
// QueryType names seventeen modules and none of them is either. Adding them
// would mean a second copy of the money arithmetic that already computes the
// deal sheet for the candidate portal, and two copies of that drift. The cost
// of reusing it is this extra link and that these operations answer to
// ::Ability rather than Maestro::Ability.
const candidateHttpLinkFunc = (url) => {
	return createHttpLink({
		uri: `${url}${PATHS.common.candidate_graphql}`,
		fetchOptions: {},
	})
}

// CSV exports likewise: `exportCsvFile`, `exportFile` and `entityExportFields`
// live on the main app's recruiter schema, and the Maestro JWT is accepted there.
// Operations opt in with `context: { clientName: 'hostRecruiter' }`.
const hostRecruiterHttpLinkFunc = (url) => {
	return createHttpLink({
		uri: `${url}${PATHS.common.host_recruiter_graphql}`,
		fetchOptions: {},
	})
}

/*
 * One client per API URL, not one per query.
 *
 * `apolloClient()` is called at 384 sites, almost all of them as
 * `apolloClient().query(...)` — so every single request used to construct a new
 * ApolloClient, a fresh InMemoryCache and a fresh link chain, then throw them
 * away. Nothing about that chain varies per call: the URL comes from appStore
 * and the auth header is read per-request inside `authLink`'s setContext, so a
 * rotated token is picked up by the shared client exactly as it was by a new one.
 *
 * Keyed by URL so a tenant switch still gets its own client rather than talking
 * to the previous tenant's backend.
 */
let cachedClient = null
let cachedClientUrl = null

const resolveApiUrl = () => appStore.apiUrl || sessionStorage.getItem('apiUrl')

const buildClient = (url) => {
	const publicHttpLink = publicHttpLinkFunc(url)
	const httpLink = httpLinkFunc(url)
	const candidateHttpLink = candidateHttpLinkFunc(url)
	const hostRecruiterHttpLink = hostRecruiterHttpLinkFunc(url)

	return new ApolloClient({
		link: ApolloLink.split(
			(operation) => operation.getContext().clientName === 'public',
			ApolloLink.from([timeout, authLink, errorLink, publicHttpLink]), // if above
			ApolloLink.split(
				(operation) => operation.getContext().clientName === 'candidate',
				ApolloLink.from([timeout, authLink, errorLink, candidateHttpLink]),
				ApolloLink.split(
					(operation) => operation.getContext().clientName === 'hostRecruiter',
					ApolloLink.from([timeout, authLink, errorLink, hostRecruiterHttpLink]),
					ApolloLink.from([timeout, authLink, errorLink, httpLink])
				)
			)
		),
		cache: new InMemoryCache({ addTypename: false }),
		defaultOptions: defaultOptions,
	})
}

export const apolloClient = () => {
	const url = resolveApiUrl()

	if (!url) {
		return new ApolloClient(undefined)
	}

	if (!cachedClient || cachedClientUrl !== url) {
		cachedClient = buildClient(url)
		cachedClientUrl = url
	}

	return cachedClient
}

/*
 * The client handed to <ApolloProvider>. It used to be a second, hand-copied
 * version of the above that had drifted: it was missing `candidateHttpLink`
 * entirely, so a `clientName: 'candidate'` operation issued through the provider
 * would have gone to the recruiter endpoint. Returning the shared client removes
 * the copy and the drift with it.
 *
 * Still returns undefined when there is no API URL — `App` renders its spinner
 * on a falsy client, and a client pointed at nothing would fail every request.
 */
export const initializeApolloClient = async () => {
	// Tenant config is already fetched in appStore.init() for better performance
	return resolveApiUrl() ? apolloClient() : undefined
}
